import React, { useMemo, useState } from 'react';
import { Layout, Layers, Globe, ShoppingBag, Users, BarChart3, Mail, Star, Settings, FileText, ChevronDown, ChevronRight } from 'lucide-react';

const PAGE_ICONS = {
  hero: Globe,
  nav: Layout,
  navbar: Layout,
  header: Layout,
  features: Star,
  feature: Star,
  pricing: BarChart3,
  testimonial: Users,
  testimonials: Users,
  contact: Mail,
  footer: Layers,
  cta: ShoppingBag,
  about: FileText,
  services: Settings,
  dashboard: BarChart3,
  blog: FileText,
  faq: FileText,
  team: Users,
  portfolio: Layers,
  default: Layout,
};

function getPageIcon(name) {
  const lower = (name || '').toLowerCase();
  for (const [key, Icon] of Object.entries(PAGE_ICONS)) {
    if (lower.includes(key)) return Icon;
  }
  return PAGE_ICONS.default;
}

function generateBrandName(projectName, componentName) {
  if (!componentName) return projectName || 'Untitled';
  const clean = componentName
    .replace(/\.(jsx|tsx|js|ts)$/i, '')
    .replace(/([A-Z])/g, ' $1')
    .replace(/[-_]/g, ' ')
    .trim();
  return clean.split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
}

function generateLogoInitials(brandName) {
  const words = (brandName || '').split(/\s+/).filter(Boolean);
  if (words.length >= 2) {
    return (words[0][0] + words[1][0]).toUpperCase();
  }
  return (brandName || 'P').slice(0, 2).toUpperCase();
}

const BRAND_COLORS = [
  'from-violet-500 to-purple-600',
  'from-emerald-500 to-teal-600',
  'from-blue-500 to-indigo-600',
  'from-amber-500 to-orange-600',
  'from-rose-500 to-pink-600',
  'from-cyan-500 to-sky-600',
];

function PagesBrandPanel({ files, projectName, selectedFile, onSelect }) {
  const pages = useMemo(() => {
    if (!files || !Object.keys(files).length) return [];
    const EXCLUDED_DIRS = ['ui/', 'hooks/', 'lib/', 'utils/'];
    const EXCLUDED_FILES = ['index.js', 'index.ts', 'index.jsx', 'index.tsx'];
    const componentFiles = Object.keys(files).filter((f) => {
      if (!f.startsWith('src/components/') || !/\.(jsx|tsx|js|ts)$/i.test(f)) return false;
      const relPath = f.slice('src/components/'.length);
      if (EXCLUDED_DIRS.some((d) => relPath.startsWith(d))) return false;
      const fileName = f.split('/').pop();
      if (EXCLUDED_FILES.includes(fileName)) return false;
      if (/^use[A-Z-]/.test(fileName)) return false;
      if (/^made-with-/i.test(fileName)) return false;
      return true;
    });
    return componentFiles.map((filePath, index) => {
      const fileName = filePath.split('/').pop();
      const brandName = generateBrandName(projectName, fileName);
      const Icon = getPageIcon(fileName);
      const initials = generateLogoInitials(brandName);
      const colorClass = BRAND_COLORS[index % BRAND_COLORS.length];
      return { filePath, fileName, brandName, Icon, initials, colorClass };
    });
  }, [files, projectName]);

  const [expanded, setExpanded] = useState(true);

  if (!pages.length) return null;

  return (
    <div className="mb-4" data-testid="pages-brand-panel">
      <button
        onClick={() => setExpanded(!expanded)}
        className="mb-2 flex w-full items-center gap-2 px-1 text-left"
      >
        {expanded ? <ChevronDown className="h-3 w-3 text-zinc-500" /> : <ChevronRight className="h-3 w-3 text-zinc-500" />}
        <Layers className="h-3.5 w-3.5 text-violet-400" />
        <span className="text-[11px] font-semibold uppercase tracking-wider text-zinc-400">
          Pages
        </span>
        <span className="ml-auto rounded-full bg-violet-500/20 px-1.5 py-0.5 text-[10px] text-violet-300">
          {pages.length}
        </span>
      </button>
      {expanded && <div className="space-y-1">
        {pages.map((page) => {
          const isSelected = selectedFile === page.filePath;
          return (
            <button
              key={page.filePath}
              onClick={() => onSelect(page.filePath)}
              className={`flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left transition-all ${
                isSelected
                  ? 'bg-violet-500/15 border border-violet-500/30'
                  : 'hover:bg-white/5 border border-transparent'
              }`}
              data-testid={`page-brand-${page.fileName}`}
            >
              <div className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-gradient-to-br ${page.colorClass} shadow-sm`}>
                <span className="text-[9px] font-bold text-white">{page.initials}</span>
              </div>
              <div className="min-w-0 flex-1">
                <div className={`truncate text-xs font-medium ${isSelected ? 'text-violet-200' : 'text-zinc-200'}`}>
                  {page.brandName}
                </div>
                <div className="truncate text-[10px] text-zinc-500">
                  {page.fileName}
                </div>
              </div>
              <page.Icon className={`h-3.5 w-3.5 shrink-0 ${isSelected ? 'text-violet-400' : 'text-zinc-600'}`} />
            </button>
          );
        })}
      </div>}
    </div>
  );
}

export default PagesBrandPanel;
